// Mock calculation functions for debt consolidation calculator

export const calculateWithOffset = (mortgage, consolidationRate, annualIncome, monthlyExpenses, offsetDeposits, offsetWithdrawals, paymentFrequency = 'monthly') => {
  const monthlyIncome = annualIncome / 12;
  const monthlyOffsetContribution = monthlyIncome - monthlyExpenses;
  
  // Calculate payment frequency multiplier
  let paymentsPerYear = 12;
  let paymentLabel = 'Monthly';
  if (paymentFrequency === 'weekly') {
    paymentsPerYear = 52;
    paymentLabel = 'Weekly';
  } else if (paymentFrequency === 'fortnightly') {
    paymentsPerYear = 26;
    paymentLabel = 'Fortnightly';
  }
  
  // Calculate without offset
  const monthlyRate = consolidationRate / 100 / 12;
  const totalMonths = mortgage.remainingTerm * 12;
  const monthlyPayment = (mortgage.remainingBalance * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
                        (Math.pow(1 + monthlyRate, totalMonths) - 1);
  
  // Adjust payment amount based on frequency (for more frequent payments, save interest)
  const periodRate = consolidationRate / 100 / paymentsPerYear;
  const totalPeriods = mortgage.remainingTerm * paymentsPerYear;
  const periodPayment = (mortgage.remainingBalance * periodRate * Math.pow(1 + periodRate, totalPeriods)) / 
                        (Math.pow(1 + periodRate, totalPeriods) - 1);
  
  let balance = mortgage.remainingBalance;
  let totalInterestWithoutOffset = 0;
  
  for (let month = 0; month < totalMonths && balance > 0; month++) {
    const interest = balance * monthlyRate;
    totalInterestWithoutOffset += interest;
    const principal = monthlyPayment - interest;
    balance -= principal;
  }
  
  // Calculate with offset
  balance = mortgage.remainingBalance;
  let offsetBalance = 0;
  let totalInterestWithOffset = 0;
  let monthsPaid = 0;
  const monthlyOffsetData = [];
  
  // Sort deposits and withdrawals by month
  const sortedDeposits = [...offsetDeposits].sort((a, b) => a.month - b.month);
  const sortedWithdrawals = [...offsetWithdrawals].sort((a, b) => a.month - b.month);
  
  for (let month = 1; month <= totalMonths && balance > 0; month++) {
    // Add regular monthly contribution (income minus expenses)
    offsetBalance += monthlyOffsetContribution;
    
    // Add any additional deposits for this month
    sortedDeposits.forEach(deposit => {
      if (deposit.month === month) {
        offsetBalance += deposit.amount;
      }
    });
    
    // Subtract any withdrawals for this month
    sortedWithdrawals.forEach(withdrawal => {
      if (withdrawal.month === month) {
        offsetBalance = Math.max(0, offsetBalance - withdrawal.amount);
      }
    });
    
    // Subtract mortgage payment from offset account
    // Payment comes out based on frequency
    const periodsThisMonth = paymentFrequency === 'weekly' ? 4.33 : paymentFrequency === 'fortnightly' ? 2.17 : 1;
    const monthlyPaymentAmount = periodPayment * periodsThisMonth;
    offsetBalance = Math.max(0, offsetBalance - monthlyPaymentAmount);
    
    // Calculate effective balance (loan balance minus offset balance)
    const effectiveBalance = Math.max(0, balance - offsetBalance);
    const interest = effectiveBalance * monthlyRate;
    totalInterestWithOffset += interest;
    
    const principal = monthlyPaymentAmount - interest;
    balance -= principal;
    monthsPaid = month;
    
    // Store data for first 12 months
    if (month <= 12) {
      monthlyOffsetData.push({
        month,
        offsetBalance: Math.round(offsetBalance),
        loanBalance: Math.round(balance),
        effectiveBalance: Math.round(effectiveBalance),
        interestCharged: Math.round(interest),
        interestSaved: Math.round((balance * monthlyRate) - interest)
      });
    }
  }
  
  const interestSaved = totalInterestWithoutOffset - totalInterestWithOffset;
  const timeSaved = totalMonths - monthsPaid;
  
  return {
    withoutOffset: {
      totalInterest: totalInterestWithoutOffset,
      monthlyPayment,
      totalMonths
    },
    withOffset: {
      totalInterest: totalInterestWithOffset,
      monthlyPayment,
      periodPayment,
      monthsPaid,
      interestSaved,
      timeSaved: Math.round(timeSaved),
      yearsSaved: (timeSaved / 12).toFixed(1)
    },
    monthlyOffsetData,
    monthlyOffsetContribution,
    paymentFrequency,
    paymentLabel
  };
};

export const calculateDebtConsolidation = (debts, mortgage, consolidationRate) => {
  // Calculate current debt scenario
  const totalDebtAmount = debts.reduce((sum, debt) => sum + debt.amount, 0);
  const weightedDebtRate = debts.reduce((sum, debt) => sum + (debt.amount * debt.rate), 0) / totalDebtAmount;
  
  // Calculate monthly payments for current debts
  const currentMonthlyPayments = debts.reduce((sum, debt) => {
    const monthlyRate = debt.rate / 100 / 12;
    const months = debt.term * 12;
    const payment = (debt.amount * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                    (Math.pow(1 + monthlyRate, months) - 1);
    return sum + payment;
  }, 0);
  
  // Calculate total interest on current debts
  const totalCurrentInterest = debts.reduce((sum, debt) => {
    const monthlyRate = debt.rate / 100 / 12;
    const months = debt.term * 12;
    const payment = (debt.amount * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                    (Math.pow(1 + monthlyRate, months) - 1);
    return sum + (payment * months - debt.amount);
  }, 0);
  
  // Calculate consolidated scenario
  const newMortgageAmount = mortgage.remainingBalance + totalDebtAmount;
  const consolidatedMonthlyRate = consolidationRate / 100 / 12;
  const remainingMonths = mortgage.remainingTerm * 12;
  
  const consolidatedPayment = (newMortgageAmount * consolidatedMonthlyRate * 
                               Math.pow(1 + consolidatedMonthlyRate, remainingMonths)) / 
                              (Math.pow(1 + consolidatedMonthlyRate, remainingMonths) - 1);
  
  const consolidatedTotalInterest = consolidatedPayment * remainingMonths - newMortgageAmount;
  
  // Calculate original mortgage payment
  const originalMortgageRate = mortgage.interestRate / 100 / 12;
  const originalMortgagePayment = (mortgage.remainingBalance * originalMortgageRate * 
                                   Math.pow(1 + originalMortgageRate, remainingMonths)) / 
                                  (Math.pow(1 + originalMortgageRate, remainingMonths) - 1);
  
  // Calculate savings
  const monthlySavings = (currentMonthlyPayments + originalMortgagePayment) - consolidatedPayment;
  const totalInterestSavings = totalCurrentInterest - (consolidatedTotalInterest - 
                               (originalMortgagePayment * remainingMonths - mortgage.remainingBalance));
  
  // Calculate redraw strategy scenario
  const redrawExtraPayment = monthlySavings;
  const redrawMonthlyPayment = consolidatedPayment + redrawExtraPayment;
  
  let redrawBalance = newMortgageAmount;
  let redrawMonths = 0;
  let redrawTotalInterest = 0;
  
  while (redrawBalance > 0 && redrawMonths < remainingMonths) {
    const monthInterest = redrawBalance * consolidatedMonthlyRate;
    redrawTotalInterest += monthInterest;
    const principal = redrawMonthlyPayment - monthInterest;
    redrawBalance -= principal;
    redrawMonths++;
  }
  
  const timesSaved = remainingMonths - redrawMonths;
  const additionalInterestSavings = consolidatedTotalInterest - redrawTotalInterest;
  
  return {
    current: {
      totalDebtAmount,
      monthlyDebtPayments: currentMonthlyPayments,
      monthlyMortgagePayment: originalMortgagePayment,
      totalMonthlyPayment: currentMonthlyPayments + originalMortgagePayment,
      totalInterest: totalCurrentInterest,
      averageRate: weightedDebtRate
    },
    consolidated: {
      newMortgageAmount,
      monthlyPayment: consolidatedPayment,
      totalInterest: consolidatedTotalInterest,
      monthlySavings,
      totalInterestSavings
    },
    redrawStrategy: {
      monthlyPayment: redrawMonthlyPayment,
      totalInterest: redrawTotalInterest,
      monthsSaved: timesSaved,
      yearsSaved: (timesSaved / 12).toFixed(1),
      additionalSavings: additionalInterestSavings,
      totalSavings: totalInterestSavings + additionalInterestSavings
    }
  };
};

export const mockDebts = [
  { id: 1, name: 'Personal Loan', amount: 15000, rate: 12.5, term: 3, debt_type: 'Personal loan', min_payment: 502, lender: '' },
  { id: 2, name: 'Credit Card 1', amount: 8000, rate: 19.9, term: 2, debt_type: 'Credit card', min_payment: 240, lender: '' }
];

export const mockMortgage = {
  remainingBalance: 350000,
  interestRate: 6.2,
  remainingTerm: 25
};